import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { Router } from './router';
import { Storage, AccountManager, api, showToast, EventBus, TaskTracker } from './utils';

interface NavItem {
  key: string;
  icon: string;
  label: string;
  path?: string;
  badge?: string;
}

export class MangaApp extends LitElement {
  static override styles = css`
    *, *::before, *::after { box-sizing: border-box; }
    * { scrollbar-width: none; -ms-overflow-style: none; }
    ::-webkit-scrollbar { display: none; }
    :host {
      display: flex;
      width: 100%;
      height: 100dvh;
      font-family: var(--font-body);
      color: var(--text-primary);
    }

    /* ===== Sidebar ===== */
    .sidebar {
      width: 248px;
      background: var(--glass-bg-strong);
      backdrop-filter: blur(var(--glass-blur));
      -webkit-backdrop-filter: blur(var(--glass-blur));
      border-right: 1px solid var(--border-color);
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 50;
      overflow: hidden;
    }
    .sidebar.collapsed { width: 64px; }
    .collapsed .nav-text, .collapsed .nav-group-label,
    .collapsed .nav-badge, .collapsed .brand-text,
    .collapsed .brand-tagline { display: none; }
    .collapsed .nav-item { justify-content: center; padding: 10px; }
    .collapsed .brand { justify-content: center; padding: 14px 0; gap: 0; }
    .collapsed .brand-logo { width: 28px; height: 28px; }

    /* Brand */
    .brand {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 20px 18px 8px;
      cursor: pointer;
      transition: opacity 0.2s ease;
    }
    .brand:hover { opacity: 0.85; }
    .brand-logo {
      width: 38px; height: 38px;
      object-fit: contain;
      flex-shrink: 0;
      filter: drop-shadow(0 2px 6px rgba(45, 38, 84, 0.2));
      transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    .brand:hover .brand-logo { transform: scale(1.1); filter: drop-shadow(0 4px 12px rgba(45, 38, 84, 0.3)); }
    .brand-info { display: flex; flex-direction: column; min-width: 0; }
    .brand-text {
      font-size: 1.1rem;
      font-weight: 800;
      letter-spacing: 0.02em;
      background: linear-gradient(135deg, #2d2654 0%, #5b4a8a 40%, #c4a77d 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      line-height: 1.2;
    }
    .brand-tagline {
      font-size: 0.58rem;
      color: var(--text-tertiary);
      letter-spacing: 0.08em;
      font-weight: 500;
      margin-top: 2px;
      text-transform: uppercase;
    }

    /* Nav */
    .nav-scroll {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 8px 10px 16px;
    }
    .nav-group-label {
      font-size: 0.6rem;
      font-weight: 700;
      color: var(--text-tertiary);
      text-transform: uppercase;
      letter-spacing: 0.1em;
      padding: 18px 8px 6px;
      user-select: none;
    }
    .nav-group-label:first-child { padding-top: 8px; }

    .nav-item {
      display: flex;
      align-items: center;
      gap: 11px;
      padding: 8px 10px;
      margin-bottom: 1px;
      border: none;
      background: transparent;
      color: var(--text-secondary);
      border-radius: var(--radius-md);
      cursor: pointer;
      font: inherit;
      font-size: 0.82rem;
      font-weight: 500;
      width: 100%;
      text-align: left;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      outline: none;
    }
    .nav-item:hover, .nav-item:focus-visible {
      background: rgba(139, 92, 246, 0.05);
      color: var(--text-primary);
    }
    .nav-item.active {
      background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(196, 167, 125, 0.06));
      color: var(--primary-500);
      font-weight: 600;
    }
    .nav-item.active::before {
      content: '';
      position: absolute; left: -1px; top: 50%;
      transform: translateY(-50%);
      width: 3px; height: 18px;
      background: linear-gradient(to bottom, var(--primary-400), var(--gold-400));
      border-radius: 0 3px 3px 0;
    }
    .nav-icon {
      font-family: 'Material Symbols Outlined';
      font-size: 1.2rem;
      width: 22px;
      text-align: center;
      flex-shrink: 0;
      transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    .nav-item:hover .nav-icon { transform: scale(1.1); }
    .nav-item.active .nav-icon { transform: scale(1.05); }
    .nav-text { flex: 1; white-space: nowrap; overflow: hidden; }
    .nav-badge {
      font-size: 0.58rem; padding: 1px 6px;
      border-radius: var(--radius-full);
      background: linear-gradient(135deg, rgba(139,92,246,0.15), rgba(196,167,125,0.1));
      color: var(--primary-500);
      font-weight: 700; flex-shrink: 0;
    }

    /* Sidebar footer */
    .sidebar-footer {
      padding: 6px 10px 10px;
      border-top: 1px solid var(--border-color);
    }
    .collapse-btn {
      display: flex; align-items: center; justify-content: center; gap: 6px;
      width: 100%; padding: 7px;
      border: none; background: transparent;
      color: var(--text-tertiary); cursor: pointer;
      border-radius: var(--radius-md);
      transition: all 0.2s ease;
      font-size: 0.75rem;
      font-family: inherit;
      outline: none;
    }
    .collapse-btn:hover, .collapse-btn:focus-visible {
      background: rgba(139, 92, 246, 0.05);
      color: var(--text-secondary);
    }
    .collapse-btn .mi {
      font-family: 'Material Symbols Outlined';
      font-size: 16px;
      transition: transform 0.3s ease;
    }
    .sidebar.collapsed .collapse-btn .mi { transform: rotate(180deg); }
    .logout-btn {
      display: flex; align-items: center; justify-content: center; gap: 6px;
      width: 100%; padding: 7px;
      border: none; background: transparent;
      color: var(--text-tertiary); cursor: pointer;
      border-radius: var(--radius-md);
      transition: all 0.2s ease;
      font-size: 0.75rem;
      font-family: inherit;
      outline: none;
      margin-bottom: 4px;
    }
    .logout-btn:hover { background: rgba(239,68,68,0.06); color: #dc2626; }
    .billing-bar {
      display: flex; align-items: center; gap: 6px;
      padding: 8px 10px; margin-bottom: 6px;
      background: linear-gradient(135deg, rgba(139,92,246,0.06), rgba(196,167,125,0.04));
      border-radius: var(--radius-md); border: 1px solid rgba(139,92,246,0.1);
      font-size: 0.72rem; color: var(--text-secondary); cursor: pointer;
      transition: all 0.2s;
      flex-wrap: wrap; justify-content: center;
    }
    .billing-bar:hover { background: rgba(139,92,246,0.1); border-color: rgba(139,92,246,0.2); }
    .billing-bar.conc-ok .mi { color: #22c55e; }
    .billing-bar.conc-warn .mi { color: #f59e0b; }
    .billing-bar.conc-full .mi { color: #ef4444; }
    .billing-bar.conc-full {
      background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.2);
      animation: conc-pulse 2s ease-in-out infinite;
    }
    @keyframes conc-pulse {
      0%, 100% { border-color: rgba(239,68,68,0.2); }
      50% { border-color: rgba(239,68,68,0.5); }
    }
    .billing-bar .mi { font-family: 'Material Symbols Outlined'; font-size: 14px; color: var(--primary-500); flex-shrink: 0; }
    .billing-val { font-weight: 700; color: var(--text-primary); white-space: nowrap; }
    .billing-sep { color: var(--border-color); flex-shrink: 0; }
    .billing-break { flex-basis: 100%; height: 0; }
    .collapsed .billing-bar span:not(.mi):not(.billing-val-short) { display: none; }
    .collapsed .billing-bar { justify-content: center; padding: 8px 4px; }
    .user-row {
      display: flex; align-items: center; gap: 10px;
      padding: 8px 10px; margin-bottom: 6px;
    }
    .user-avatar {
      width: 32px; height: 32px; border-radius: 50%;
      background: linear-gradient(135deg, var(--primary-500), var(--primary-600));
      display: flex; align-items: center; justify-content: center;
      font-size: 0.85rem; font-weight: 800; color: #fff;
      cursor: pointer; flex-shrink: 0; overflow: hidden;
      transition: transform 0.2s, box-shadow 0.2s;
    }
    .user-avatar:hover { transform: scale(1.1); box-shadow: 0 2px 8px rgba(139,92,246,0.3); }
    .user-name {
      font-size: 0.78rem; font-weight: 600; color: var(--text-primary);
      cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      flex: 1; min-width: 0;
    }
    .user-name:hover { color: var(--primary-600); }
    .collapsed .user-name { display: none; }
    .collapsed .user-row { justify-content: center; padding: 8px 4px; }

    .logout-btn .mi { font-family: 'Material Symbols Outlined'; font-size: 16px; }
    .collapsed .logout-btn span:not(.mi) { display: none; }

    /* ===== Main ===== */
    .main {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      min-width: 0;
      background-color: var(--bg-app);
      position: relative;
    }
    .main-header {
      height: 52px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 28px;
      background: var(--glass-bg);
      backdrop-filter: blur(var(--glass-blur));
      -webkit-backdrop-filter: blur(var(--glass-blur));
      border-bottom: 1px solid var(--border-color);
      flex-shrink: 0;
      z-index: 10;
    }
    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.85rem;
      color: var(--text-tertiary);
      font-weight: 500;
    }
    .breadcrumb-current {
      color: var(--text-primary);
      font-weight: 700;
      letter-spacing: -0.01em;
    }
    .header-actions { display: flex; align-items: center; gap: 4px; }
    .task-indicator {
      display: flex; align-items: center; gap: 6px;
      padding: 4px 12px; border-radius: 20px;
      background: rgba(99, 102, 241, 0.12); color: var(--primary-500);
      font-size: 0.78rem; font-weight: 500; cursor: pointer;
      transition: background 0.15s;
    }
    .task-indicator:hover { background: rgba(99, 102, 241, 0.22); }
    .indicator-dot {
      width: 8px; height: 8px; border-radius: 50%;
      background: var(--primary-500);
      animation: pulse-dot 1.5s ease-in-out infinite;
    }
    @keyframes pulse-dot {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(0.8); }
    }
    .header-btn {
      width: 34px; height: 34px;
      border: none; background: transparent;
      color: var(--text-secondary);
      border-radius: var(--radius-md);
      cursor: pointer; transition: var(--transition-fast);
      display: flex; align-items: center; justify-content: center;
      font-size: 1rem;
      outline: none;
    }
    .header-btn:hover, .header-btn:focus-visible {
      background: rgba(139, 92, 246, 0.06);
      color: var(--text-primary);
    }

    .main-content {
      flex: 1;
      overflow: hidden;
    }

    /* Mobile */
    .sidebar-overlay { display: none; }
    .mobile-toggle { display: none; }

    @media (max-width: 960px) {
      .sidebar {
        position: fixed;
        top: 0; left: 0; bottom: 0;
        width: min(82vw, 280px);
        transform: translateX(-105%);
        box-shadow: var(--shadow-lg);
        z-index: 200;
        transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .sidebar.mobile-open { transform: translateX(0); }
      .sidebar.collapsed { width: min(82vw, 280px); }
      .sidebar-overlay {
        display: block;
        position: fixed; inset: 0;
        background: rgba(12, 21, 40, 0.25);
        backdrop-filter: blur(3px);
        opacity: 0; visibility: hidden;
        transition: all 0.3s ease;
        z-index: 190;
      }
      .sidebar-overlay.open { opacity: 1; visibility: visible; }
      .mobile-toggle { display: flex; }
    }
  `;

  @state() private collapsed = false;
  @state() private mobileOpen = false;
  @state() private mobile = false;
  @state() private currentPage = '';
  @state() private billingInfo: {balance:number; running:number; max_concurrency:number; storage_used?:number; max_storage_gb?:number; storage_limit_bytes?:number; storage_daily_cost?:number; storage_suspended_at?:string|null} = {balance:0, running:0, max_concurrency:0};
  private _billingTimer: any = null;
  @state() private userName = '';
  @state() private userAvatar = '';
  private nav: NavItem[] = [
    { key: 'dashboard', icon: 'home', label: '工作台', path: '/' },
    { key: 'create', icon: 'add_circle', label: '创建项目', path: '/create' },
    { key: 'projects', icon: 'folder_open', label: '项目管理', path: '/projects' },
    { key: 'regular', icon: 'movie', label: '常规漫剧', path: '/project/regular' },
    { key: 'commentary', icon: 'mic', label: '解说漫剧', path: '/project/commentary' },
    { key: 'char-lib', icon: 'person', label: '角色库', path: '/library/characters' },
    { key: 'scene-lib', icon: 'landscape', label: '场景库', path: '/library/scenes' },
    { key: 'prop-lib', icon: 'swords', label: '道具库', path: '/library/props' },
    { key: 'prompts', icon: 'auto_awesome', label: '提示词模板', path: '/library/prompts' },
    { key: 'voice-lib', icon: 'record_voice_over', label: '音色库', path: '/library/voices' },
    { key: 'dubbing', icon: 'headphones', label: '配音工作台', path: '/dubbing/select' },
    { key: 'billing', icon: 'account_balance_wallet', label: '我的账单', path: '/billing' },
    { key: 'model-selector', icon: 'smart_toy', label: '模型选择', path: '/model-selector' },
    { key: 'tasks', icon: 'assignment', label: '任务记录', path: '/tasks' },
  ];

  private readonly handleResize = () => {
    const m = window.innerWidth <= 960;
    if (m !== this.mobile) { this.mobile = m; this.mobileOpen = false; }
  };

  constructor() {
    super();
    this.collapsed = Storage.get<boolean>('sidebarCollapsed', false);
  }

  private toggle() {
    if (this.mobile) { this.mobileOpen = !this.mobileOpen; }
    else { this.collapsed = !this.collapsed; Storage.set('sidebarCollapsed', this.collapsed); }
  }

  private async nav_to(path: string, key?: string) {
    if (key === 'create') {
      const c = Router.getCurrentPath();
      const isOnCreateSubPage = (c === '/project/regular') || (c === '/project/commentary') || (c === '/project/new') || (c === '/create');
      if (isOnCreateSubPage) { if (this.mobile) this.mobileOpen = false; return; }
      Router.navigate('/create');
      if (this.mobile) this.mobileOpen = false;
      return;
    }
    if (key === 'regular') {
      const lastId = localStorage.getItem('lastRegularProject');
      if (lastId) {
        try {
          const res = await api(`/api/projects/${lastId}`, { silent: true });
          if (res.ok) {
            Router.navigate(`/project/regular/${lastId}`);
          } else {
            localStorage.removeItem('lastRegularProject');
            Router.navigate('/projects');
          }
        } catch {
          Router.navigate(`/project/regular/${lastId}`);
        }
      } else {
        Router.navigate('/projects');
      }
    } else if (key === 'commentary') {
      const lastPath = localStorage.getItem('lastCommentaryPath');
      const lastId = localStorage.getItem('lastCommentaryProject');
      const candidateId = lastPath?.match(/\/([\w-]+)$/)?.[1] || lastId;
      if (candidateId) {
        try {
          const res = await api(`/api/projects/${candidateId}`, { silent: true });
          if (res.ok) {
            Router.navigate(lastPath || `/project/commentary-storyboard/${candidateId}`);
          } else {
            localStorage.removeItem('lastCommentaryPath');
            localStorage.removeItem('lastCommentaryProject');
            showToast('您尚未打开过解说漫剧应用', 'info');
          }
        } catch {
          Router.navigate(lastPath || `/project/commentary-storyboard/${candidateId}`);
        }
      } else {
        showToast('您尚未打开过解说漫剧应用', 'info');
      }
    } else {
      Router.navigate(path);
    }
    if (this.mobile) this.mobileOpen = false;
  }

  private isActive(path?: string, key?: string): boolean {
    if (!path && !key) return false;
    const c = Router.getCurrentPath();
    if (key === 'regular') return c.startsWith('/project/regular/');
    if (key === 'commentary') return c.startsWith('/project/commentary-storyboard/') || (c.startsWith('/dubbing/') && c !== '/dubbing/select');
    if (path === '/') return c === '/' || c === '/dashboard';
    if (path === '/create') return c === '/create' || c === '/project/regular' || c === '/project/new' || c === '/project/commentary';
    return c === path || c.startsWith(path + '/');
  }

  private renderNavItem(item: NavItem): unknown {
    const active = this.isActive(item.path, item.key);
    return html`
      <button class="nav-item ${active ? 'active' : ''}"
        @click=${() => this.nav_to(item.path!, item.key)}
        aria-current=${active ? 'page' : 'false'}>
        <span class="nav-icon" aria-hidden="true">${item.icon}</span>
        <span class="nav-text">${item.label}</span>
        ${item.badge ? html`<span class="nav-badge">${item.badge}</span>` : nothing}
      </button>
    `;
  }

  private unsubRoute?: () => void;

  override connectedCallback(): void {
    super.connectedCallback();
    this.handleResize();
    window.addEventListener('resize', this.handleResize, { passive: true });
    this.unsubRoute = Router.onChange(e => {
      this.currentPage = e.path; this.requestUpdate(); this._loadBilling();
      const _p = e.path;
      if (_p === '/project/regular' || _p === '/project/new' || _p.startsWith('/project/commentary')) {
        localStorage.setItem('lastCreateSubPage', _p + (window.location.search || ''));
      }
      if (_p === '/create') {
        localStorage.removeItem('lastCreateSubPage');
      }
    });
    this._startBillingPoll();
    EventBus.on('concurrency:change', (ev: any) => {
      const d = ev.payload || ev;
      if (d.running !== undefined) {
        this.billingInfo = { ...this.billingInfo, running: d.running, max_concurrency: d.max ?? this.billingInfo.max_concurrency };
      }
    });
    EventBus.on('tasktracker:change', () => this.requestUpdate());
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.handleResize);
    this.unsubRoute?.();
    if (this._billingTimer) clearInterval(this._billingTimer);
    this._stopBillingPoll();
  }

  private _billingPollActive = false;

  private _startBillingPoll() {
    if (this._billingPollActive) return;
    this._billingPollActive = true;
    this._loadBilling();
    this._billingTimer = setInterval(() => this._loadBilling(), 30000);
    document.addEventListener('visibilitychange', this._onVisibility);
    window.addEventListener('billing:refresh', this._onBillingRefresh);
  }

  private _stopBillingPoll() {
    this._billingPollActive = false;
    if (this._billingTimer) { clearInterval(this._billingTimer); this._billingTimer = null; }
    document.removeEventListener('visibilitychange', this._onVisibility);
    window.removeEventListener('billing:refresh', this._onBillingRefresh);
  }

  private readonly _onVisibility = () => {
    if (document.hidden) {
      if (this._billingTimer) { clearInterval(this._billingTimer); this._billingTimer = null; }
    } else {
      this._loadBilling();
      if (!this._billingTimer) this._billingTimer = setInterval(() => this._loadBilling(), 30000);
    }
  };

  private readonly _onBillingRefresh = () => { this.userName = ''; this.userAvatar = ''; this._loadBilling(); };


  private get _concClass(): string {
    const avail = this.billingInfo.max_concurrency - this.billingInfo.running;
    if (avail <= 0 && this.billingInfo.max_concurrency > 0) return 'conc-full';
    if (avail <= 1) return 'conc-warn';
    return 'conc-ok';
  }
  private async _loadBilling() {
    if (!AccountManager.isLoggedIn) return;
    try {
      const res = await api('/api/billing/info');
      if (res.ok) {
        const data = await res.json();
        this.billingInfo = data;
        EventBus.emit('concurrency:change', {
          running: data.running,
          max: data.max_concurrency,
          available: data.max_concurrency - data.running,
        });
      }
    } catch {}
    if (!this.userName) {
      try {
        const r = await api('/api/accounts/me/profile');
        if (r.ok) {
          const d = await r.json();
          this.userName = d.name || d.username || '';
          this.userAvatar = d.avatar || '';
        }
      } catch {}
    }
  }

  private getBreadcrumb(): string {
    const p = Router.getCurrentPath();
    const params = Router.getParams();
    if (p === '/' || p === '/dashboard') return '工作台';
    if (p === '/create') return '创建项目';
    if (p.startsWith('/project/regular')) {
      return params.id ? `常规漫剧 · 项目编辑` : '创建项目 · 常规漫剧';
    }
    if (p.startsWith('/project/new')) return '创建项目 · 新建项目';
    if (p.startsWith('/project/commentary-storyboard')) {
      return params.id ? `解说漫剧 · 分镜剧本` : '解说漫剧';
    }
    if (p.startsWith('/project/commentary')) return '创建项目 · 解说漫剧';
    if (p.startsWith('/projects')) return '项目管理';
    if (p.startsWith('/dubbing/select')) return '配音工作台';
    if (p.startsWith('/dubbing/')) return '解说漫剧 · 配音';
    if (p.startsWith('/storyboard')) {
      return params.id ? `分镜编辑` : '分镜编辑';
    }
    if (p.startsWith('/library/characters')) return '素材库 · 角色';
    if (p.startsWith('/library/sora-characters')) return '素材库 · Sora角色';
    if (p.startsWith('/library/scenes')) return '素材库 · 场景';
    if (p.startsWith('/library/props')) return '素材库 · 道具';
    if (p.startsWith('/library/prompts')) return '素材库 · 提示词模板';
    if (p.startsWith('/library/voices')) return '素材库 · 音色库';
    if (p.startsWith('/tasks')) return '任务记录';
    if (p.startsWith('/model-selector')) return '模型选择';
    if (p.startsWith('/account')) return '账户管理';
    if (p.startsWith('/billing')) return '我的账单';
    if (p.startsWith('/admin')) return '管理后台';
    return '';
  }

  override render() {
    return html`
      <div class="sidebar-overlay ${this.mobileOpen ? 'open' : ''}" @click=${() => { this.mobileOpen = false; }}></div>

      <aside class="sidebar ${this.collapsed ? 'collapsed' : ''} ${this.mobileOpen ? 'mobile-open' : ''}">
        <div class="brand" @click=${() => this.nav_to('/')}>
          <img class="brand-logo" src="/logo.png" alt="大言">
          <div class="brand-info">
            <span class="brand-tagline">AI Comic Studio</span>
          </div>
        </div>

        <div class="nav-scroll">
          <div class="nav-group-label">概览</div>
          ${this.renderNavItem(this.nav[0])}

          <div class="nav-group-label">创作</div>
          ${this.nav.slice(1, 5).map(i => this.renderNavItem(i))}

          <div class="nav-group-label">素材库</div>
          ${this.nav.slice(5, 11).map(i => this.renderNavItem(i))}

          <div class="nav-group-label">系统</div>
          ${this.nav.slice(11).map(i => this.renderNavItem(i))}
          ${AccountManager.isAdmin ? this.renderNavItem({ key: 'admin', icon: 'admin_panel_settings', label: '管理后台', path: '/admin' }) : nothing}
        </div>

        <div class="sidebar-footer">
          ${AccountManager.isLoggedIn ? html`
            <div class="user-row">
              <div class="user-avatar" @click=${() => Router.navigate('/account')}>
                ${this.userAvatar
                  ? html`<img src="${this.userAvatar}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" @error=${() => { this.userAvatar = ''; }}>`
                  : (this.userName || '?')[0].toUpperCase()}
              </div>
              <span class="user-name" @click=${() => Router.navigate('/account')}>${this.userName || '用户'}</span>
              <button class="logout-btn" style="width:auto;margin:0;padding:4px 8px;" @click=${() => AccountManager.logout()}>
                <span class="mi">logout</span>
              </button>
            </div>
            <div class="billing-bar ${this._concClass}" @click=${() => Router.navigate('/billing')}>
              <span class="mi">bolt</span>
              <span class="billing-val">${this.billingInfo.running}/${this.billingInfo.max_concurrency}</span>
              <span class="billing-sep">|</span>
              <span class="mi">cloud</span>
              <span class="billing-val">${this.billingInfo.storage_used ? (this.billingInfo.storage_used / 1073741824).toFixed(1) : '0'}/${this.billingInfo.max_storage_gb || 1}G</span>
              <span class="billing-break"></span>
              <span class="mi">paid</span>
              <span class="billing-val">¥${this.billingInfo.balance.toFixed(2)}</span>
            </div>

          ` : nothing}
          <button class="collapse-btn" @click=${() => this.toggle()}>
            <span class="mi" aria-hidden="true">chevron_left</span>
            ${this.collapsed ? nothing : html`<span>收起侧栏</span>`}
          </button>
        </div>
      </aside>

      <div class="main">
        <header class="main-header">
          <div style="display:flex;align-items:center;gap:10px;">
            <button class="header-btn mobile-toggle" @click=${() => this.toggle()}>
              <span class="mi" style="font-family:'Material Symbols Outlined';font-size:20px">menu</span>
            </button>
            <div class="breadcrumb">
              <span class="breadcrumb-current">${this.getBreadcrumb()}</span>
            </div>
          </div>
          <div class="header-actions">
            ${TaskTracker.activeCount() > 0 ? html`
              <div class="task-indicator" @click=${() => Router.go('/tasks')} title="\u70b9\u51fb\u67e5\u770b\u4efb\u52a1\u8bb0\u5f55">
                <span class="indicator-dot"></span>
                <span>${TaskTracker.activeCount()} \u4e2a\u4efb\u52a1\u8fd0\u884c\u4e2d</span>
              </div>
            ` : nothing}
          </div>
        </header>
        <div class="main-content">
          <slot></slot>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('manga-app')) {
  customElements.define('manga-app', MangaApp);
}

declare global {
  interface HTMLElementTagNameMap {
    'manga-app': MangaApp;
  }
}
